import {
  ArrowIcon,
  Box,
  Button,
  DestructiveIcon,
  Divider,
  Flex,
  Heading,
  InlineAlert,
  ModalTitle,
  Text,
  TextButton,
  useOpenModal,
} from '@modusign/design-system';
import { useAttachments, useDocument, useSigners, useVerification } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import { Host, Signer, Verification } from '@modusign/react-signing/types';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCopyToClipboard } from 'react-use';
import { DeepReadonly } from 'ts-essentials';

import Breakpoint from '@app/components/Breakpoint';
import { CheckIcon } from '@app/components/Icons';
import InPersonHostInfoBadge from '@app/components/InPersonHostInfoBadge/index';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import { FullWidthButtonAlertModalWithCheckbox } from '@app/components/Modal/FullWidthButtonAlertModalWithCheckbox';
import Table from '@app/components/Table';
import * as BREAKPOINT from '@app/constants/breakpoint';
import DocumentAutoSharingContent from '@app/containers/Modal/EditorContractSetupPreviewModal/DocumentAutoSharingContent';
import DocumentSharingContent from '@app/containers/Modal/EditorContractSetupPreviewModal/DocumentSharingContent';
import { formatContact } from '@app/features/apply-react-signing/libs/formatter/formatContact';
import { ResourceRoleSubject } from '@app/features/authorization';
import { useDocumentPermission } from '@app/features/documents/hooks/useDocumentPermission';
import useGetLabelListByIds from '@app/features/label/hooks/useGetLabelListByIds';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import useUsageCount from '@app/hooks/useUsageCount';
import { useUserScopedLocalStorage } from '@app/hooks/useUserScopedLocalStorage';
import { useIsPublicWorkspace } from '@app/hooks/workspace';
import formatScheduledDate from '@app/libs/formatter/formatScheduledDate';
import workspaceService from '@app/libs/workspaceService';
import { contractSetupLabelIdsChange } from '@app/store/contract-setup/actions';
import { useSubmitContractSetup } from '@app/store/contract-setup/hooks/useSubmitContractSetup';
import { getCarbonCopiesData } from '@app/store/contract-setup/reducers/carbonCopies';
import {
  getIsCheckSchedule,
  getLabelIds,
  getScheduledDate,
} from '@app/store/contract-setup/selectors';
import { modalClose } from '@app/store/modal/actions';

import DocumentLabelSelectionContent from './DocumentLabelSelectionContent';
import SigningMethodTypeInfo from './SigningMethodTypeInfo';
import Title from './Title';

import './EditorContractSetupPreviewModal.scss';

const getVerificationNameList = (
  verification: DeepReadonly<Verification> | undefined
): string[] => {
  const verificationNames: Array<string> = [];

  if (verification?.methods.some((method) => method.type === 'PASSWORD')) {
    verificationNames.push('접근 암호 인증');
  }

  if (verification?.methods.some((method) => method.type === 'MOBILE_IDENTIFICATION')) {
    verificationNames.push('휴대폰 본인 인증');
  }

  if (verification?.methods.some((method) => method.type === 'D_CERT')) {
    verificationNames.push('법인 공동인증서 인증');
  }

  return verificationNames;
};

function getLocaleByHostAndSigner(signer: Signer, host?: Host) {
  // (@Juyeong-Byeon) 대면서명의 서명 요청은 host에게 가기때문에 서명자가 아닌 진행자의 locale을 이용해야한다.
  return signer.signingMethod === 'IN_PERSON' && host ? host.locale : signer.locale;
}

function VerificationMethodDisplayName({ signerId }: { signerId: string }) {
  const { verification } = useVerification(signerId);

  const verificationNameList = getVerificationNameList(verification);

  return verificationNameList.length === 0 ? (
    <> 설정 안함</>
  ) : (
    <span>
      {verificationNameList.map((message, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Text as="p" key={`${index}-${message}`} whiteSpace="nowrap">
          {message}
        </Text>
      ))}
    </span>
  );
}

function VerificationMethodDisplayNameMobileView({ signerId }: { signerId: string }) {
  const { verification } = useVerification(signerId);

  return (
    <Text as="div" color="textNeutralLight" fontSize="xs" mt={1}>
      {getVerificationNameList(verification).join(', ')}
      <Box display="inline-block" ml={2}>
        <CheckIcon color="#bdbdbd" />
      </Box>
    </Text>
  );
}
const convertRoleSubjects = (roles?: ResourceRoleSubject[]) => {
  return roles?.map((r) => ({
    subjectId: r.subjectId,
    subjectType: r.subjectType,
    name: 'VIEWER',
  }));
};

function EditorContractSetupPreviewModal() {
  const openModal = useOpenModal();
  const dispatch = useDispatch();

  const carbonCopiesData = useSelector(getCarbonCopiesData);
  const scheduledDate = useSelector(getScheduledDate);
  const isCheckSchedule = useSelector(getIsCheckSchedule);
  const labelIds = useSelector(getLabelIds);

  const { assignDocumentPermission } = useDocumentPermission();

  const isPublicWorkspace = useIsPublicWorkspace();
  const usageCount = useUsageCount();

  const carbonCopiesText = carbonCopiesData.map(({ contact }) => `<${contact}>`).join(', ');

  const { document } = useDocument();
  const { attachments } = useAttachments();
  const { signers } = useSigners();
  const { ui } = useUIState();

  const { isLoading, mutate: submitContractSetup } = useSubmitContractSetup();
  const [, copyToClipboard] = useCopyToClipboard();

  const isMobileWidth = useResponsiveValue([true, true, false]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const isScheduled = !!scheduledDate && isCheckSchedule;
  const [roleSubjects, setRoleSubjects] = useState<ResourceRoleSubject[] | undefined>();
  const requesterAttachments = attachments.filter(
    (attachment) => attachment.participantReference.type === 'REQUESTER'
  );

  const [disableContractSetupLabelAlertModal, setDisableContractSetupLabelAlertModal] =
    useUserScopedLocalStorage('contractSetupLabelAlertModal', false);

  const { labels: selectedLabels } = useGetLabelListByIds({
    labelIds,
  });

  const setSelectedLabelIds = (labelIds: string[] | undefined) => {
    dispatch(
      contractSetupLabelIdsChange({
        labelIds: labelIds ?? [],
      })
    );
  };
  const handleClose = useCallback(() => {
    dispatch(modalClose());
  }, [dispatch]);

  const handleContractSetupSubmit = () => {
    if (!selectedLabels.length && !disableContractSetupLabelAlertModal) {
      openModal((modalProps) => (
        <FullWidthButtonAlertModalWithCheckbox
          {...modalProps}
          cancelButtonLabel="라벨 설정"
          checkBoxLabel="이 알림 다시 보지 않기"
          confirmButtonLabel={isScheduled ? '이대로 예약하기' : '이대로 서명 요청'}
          title="라벨 미설정"
          onCancel={() => {
            setIsPopoverOpen(true);
            modalProps.onClose();
          }}
          onCheckBoxChange={setDisableContractSetupLabelAlertModal}
          onConfirm={() => {
            submitContractSetup({
              workspaceId: workspaceService.getWorkspaceId(),
              labelIds: undefined,
              roleGrants: convertRoleSubjects(roleSubjects),
            });
            modalProps.onClose();
          }}
        >
          <Text fontSize="md" fontWeight="regular" textAlign="center">
            라벨이 없으면 문서 분류에 어려움이 발생할 수 있습니다.
            <br />
            라벨을 설정하지 않고 서명 요청하시겠어요?
          </Text>
        </FullWidthButtonAlertModalWithCheckbox>
      ));
    } else if (!isLoading) {
      submitContractSetup({
        workspaceId: workspaceService.getWorkspaceId(),
        labelIds: selectedLabels?.map((l) => l.id),
        roleGrants: convertRoleSubjects(roleSubjects),
      });
    }
  };

  const renderSignersTable = useCallback(() => {
    return (
      <Box maxHeight="330px" overflow="auto">
        <Table
          cellClassName="EditorContractSetupPreviewModal__inner-table-cell"
          className="space-0x"
          headClassName="EditorContractSetupPreviewModal__inner-table-head"
          theme="no-line"
          titles={['서명자 정보', '추가 인증 수단']}
          values={signers.map((signer, index) => {
            const { host } = signer;
            return [
              <Box key={`${signer.id}-signer-info`}>
                <Box>
                  {ui.isInOrder && `${index + 1}. `}{' '}
                  {`${signer.name} (${formatContact(signer.signingMethod, signer.contact)})`}
                  <Text ml={2}>
                    {host && <InPersonHostInfoBadge contactInfo={host.contact} name={host.name} />}
                  </Text>
                </Box>
                <SigningMethodTypeInfo
                  locale={getLocaleByHostAndSigner(signer, host) || 'ko'}
                  signingMethod={signer.signingMethod}
                />
              </Box>,
              <VerificationMethodDisplayName
                key={`${signer.id}-verification-info`}
                signerId={signer.id}
              />,
            ];
          })}
        />
      </Box>
    );
  }, [signers, ui.isInOrder]);

  const renderFileOpenPassword = useCallback(
    () => (
      <div className="EditorContractSetupPreviewModal__file-open-password">
        <span>{document.pdfPassword}</span>{' '}
        <button
          className="TextBtn TextBtn--underline"
          type="button"
          onClick={() => {
            copyToClipboard(
              `- 잠금 설정된 파일 : ${document.title} PDF파일, 이력이 담긴 감사추적 인증서 PDF파일\n- 파일 비밀번호 : ${document.pdfPassword}\n\n서명이 모두 완료된 후, 위 문서를 열람할 때 입력해야 하는 비밀번호입니다. 꼭 기억해 두세요.`
            );
          }}
        >
          복사하기
        </button>
      </div>
    ),
    [copyToClipboard, document.pdfPassword, document.title]
  );

  const [titles, values] = useMemo(() => {
    const titles = ['문서 이름'];
    const values: Array<JSX.Element | string | undefined | null> = [document.title];

    if (document.pdfPassword) {
      titles.push('문서 비밀번호');
      values.push(renderFileOpenPassword());
    }

    if (carbonCopiesText) {
      titles.push('참조자');
      values.push(carbonCopiesText);
    }

    titles.push('서명자');
    values.push(renderSignersTable());

    if (requesterAttachments.length > 0) {
      titles.push('첨부한 파일');
      values.push(
        <Text>
          {requesterAttachments
            .map((a) => (a.file?.name && a.file?.extension ? a.file.name + a.file.extension : ''))
            .join(', ')}
        </Text>
      );
    }

    if (isScheduled) {
      titles.push('전송 예약일');
      values.push(formatScheduledDate(scheduledDate));
    }

    return [titles, values];
  }, [
    document.title,
    document.pdfPassword,
    carbonCopiesText,
    renderSignersTable,
    requesterAttachments,
    isScheduled,
    renderFileOpenPassword,
    scheduledDate,
  ]);

  return (
    <>
      <div className="Container Container--max-md">
        <Flex className="Card Card--pop box-7x align-center" flexDirection="column">
          <Flex justifyContent="space-around">
            <ModalTitle icon={<DestructiveIcon color="iconPrimaryBase" />} mx="auto">
              서명 요청 정보 확인
            </ModalTitle>
          </Flex>

          <Breakpoint compare={(innerWidth) => innerWidth > BREAKPOINT.MOBILE}>
            <div className="align-left">
              <Table
                cellClassName="EditorContractSetupPreviewModal__table-cell"
                direction="vertical"
                headClassName="EditorContractSetupPreviewModal__table-head"
                titles={titles}
                values={[values]}
              />
            </div>
            <Box pt={4}>
              <DocumentLabelSelectionContent
                isPopoverOpen={isPopoverOpen}
                selectedLabels={selectedLabels}
                setIsPopoverOpen={setIsPopoverOpen}
                setSelectedLabelIds={setSelectedLabelIds}
              />
            </Box>
            <Box>
              <DocumentAutoSharingContent />
            </Box>
            {!isMobileWidth && assignDocumentPermission.isPermitted && isPublicWorkspace && (
              <Box>
                <DocumentSharingContent
                  documentName={document.title || ''}
                  roleSubjects={roleSubjects}
                  setRoleSubjects={setRoleSubjects}
                />
              </Box>
            )}
            {isScheduled && (
              <InlineAlert sx={{ mt: 4 }} variant="warning">
                예약된 문서는 전송 시점에 자동 공유 및 추가 공유 설정이 반영됩니다.
              </InlineAlert>
            )}
            <Divider my={7} />
          </Breakpoint>
          <Breakpoint compare={(innerWidth) => innerWidth <= BREAKPOINT.MOBILE}>
            <Box
              sx={{
                borderBottomColor: 'lightGray300',
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
              }}
              textAlign="initial"
            >
              <Title>문서 제목</Title>
              <Box mb={4} px={4} py={6}>
                <Text fontSize="lg" fontWeight="medium">
                  {document.title}
                </Text>
              </Box>
              {document.pdfPassword && (
                <>
                  <Title>문서 비밀번호</Title>
                  <Flex justifyContent="space-between" mb={4} px={4} py={6}>
                    <Text fontSize="lg" fontWeight="medium">
                      {document.pdfPassword}
                    </Text>
                    <TextButton
                      onClick={() => {
                        copyToClipboard(
                          `- 잠금 설정된 파일 : ${document.title} PDF파일, 이력이 담긴 감사추적 인증서 PDF파일\n- 파일 비밀번호 : ${document.pdfPassword}\n\n서명이 모두 완료된 후, 위 문서를 열람할 때 입력해야 하는 비밀번호입니다. 꼭 기억해 두세요.`
                        );
                      }}
                    >
                      복사하기
                    </TextButton>
                  </Flex>
                </>
              )}
              {carbonCopiesText && (
                <>
                  <Title>참조자</Title>
                  <Box mb={4} px={4} py={6}>
                    <Text fontSize="lg" fontWeight="medium" sx={{ wordBreak: 'keep-all' }}>
                      {carbonCopiesText}
                    </Text>
                  </Box>
                </>
              )}
              <Title>서명자</Title>
              {signers.map((signer, index) => {
                return (
                  <Box as="ul" key={signer.id} mb={4} px={4} py={6}>
                    <li>
                      <Heading as="h5" fontSize="15px" fontWeight="medium" lineHeight="21px" mb={2}>
                        {ui.isInOrder && `${index + 1}. `} {signer.name}
                      </Heading>
                      <Box lineHeight="150%">
                        {signer.signingMethod === 'KAKAO' && signer.contact
                          ? formatContact(signer.signingMethod, signer.contact)
                          : signer.contact}
                        <Text ml={2}>
                          {signer.host && (
                            <InPersonHostInfoBadge
                              contactInfo={signer.host.contact}
                              name={signer.host.name}
                            />
                          )}
                        </Text>
                      </Box>
                      <Box>
                        <VerificationMethodDisplayNameMobileView signerId={signer.id} />
                        <SigningMethodTypeInfo
                          locale={getLocaleByHostAndSigner(signer, signer.host) || 'ko'}
                          signingMethod={signer.signingMethod}
                        />
                      </Box>
                    </li>
                  </Box>
                );
              })}
              {requesterAttachments.length > 0 && (
                <>
                  <Title>첨부한 파일</Title>
                  <Box mb={4} px={4} py={6}>
                    <Text>
                      {requesterAttachments
                        .map((a) => a.file?.name + (a.file?.extension ?? ''))
                        .join(', ')}
                    </Text>
                  </Box>
                </>
              )}
            </Box>
            <Box pb={6} pt={7}>
              <DocumentLabelSelectionContent
                isPopoverOpen={isPopoverOpen}
                selectedLabels={selectedLabels}
                setIsPopoverOpen={setIsPopoverOpen}
                setSelectedLabelIds={setSelectedLabelIds}
              />
            </Box>
            <Box pb={6}>
              <DocumentAutoSharingContent />
            </Box>
            <Divider mb={[7, 6]} />
          </Breakpoint>
          <Flex flexDirection={['column', 'row']} gap={3} justifyContent={['space-between']}>
            <Flex alignItems="center" justifyContent={['start', 'center']} mb={[7, 0]}>
              <Text fontWeight="medium">
                서명 요청{' '}
                <Text color="textEmphasisBase" fontWeight="medium">
                  1건
                </Text>{' '}
                차감 예정{' '}
                <Text color="textNeutralLight">
                  (잔여 :{' '}
                  {usageCount.signingRequestCount.limit === Infinity
                    ? '무제한'
                    : `${(
                        usageCount.signingRequestCount.limit - usageCount.signingRequestCount.usage
                      ).toLocaleString()}건`}
                  )
                </Text>
              </Text>
            </Flex>
            <Flex alignItems="center" gap={3} justifyContent="center">
              <Button onClick={handleClose}>취소</Button>
              <div className="EditorContractSetupPreviewModal__tool-tip-box">
                <Button variant="primary" onClick={handleContractSetupSubmit}>
                  {isScheduled ? '예약하기' : '서명 요청하기'}
                  <ArrowIcon direction="right" ml={1} />
                </Button>
              </div>
            </Flex>
          </Flex>
        </Flex>
      </div>
      {isLoading && <GlobalLoadingSpinner overLayClassName="Overlay--active" />}
    </>
  );
}

export default EditorContractSetupPreviewModal;
